import { useEffect, useState } from "react";
import { Link, Outlet, useNavigate, useRouterState } from "@tanstack/react-router";
import {
  Building2,
  FileText,
  KeyRound,
  LayoutDashboard,
  LogOut,
  Menu,
  Moon,
  ScrollText,
  ShieldCheck,
  TerminalSquare,
  Smartphone,
  Sun,
  Users,
} from "lucide-react";
import { Badge, Button, LoadingState, cn } from "@fiskhub/ui";
import { UNAUTHENTICATED_EVENT } from "@/api/client";
import { useLogout, useSession } from "@/api/hooks";
import { BuildStamp } from "./build-stamp";
import { CertificateBanner } from "./certificate-banner";
import { CrossTenantBanner, CrossTenantProvider, useCrossTenant } from "./cross-tenant";

/**
 * The primary navigation, in groups.
 *
 * The grouping is the information, not decoration: what you set up once
 * (companies and their certificates) is separated from what runs every day
 * (devices and the invoices they file), from who may touch it and what they
 * did (users, audit log), from the integration surface (API keys, playground).
 * Dashboard stands alone because it is the view across all of them.
 *
 * Each group renders as its OWN list rather than one list with separator rows.
 * A rule drawn between `<li>`s is presentation a screen reader never sees; four
 * short lists carry the same boundary to everyone.
 */
const NAV_GROUPS = [
  [{ to: "/", label: "Dashboard", icon: LayoutDashboard, exact: true }],
  [
    { to: "/companies", label: "Companies", icon: Building2, exact: false },
    { to: "/certificates", label: "Certificates", icon: ShieldCheck, exact: false },
  ],
  [
    { to: "/devices", label: "Devices", icon: Smartphone, exact: false },
    { to: "/invoices", label: "Invoices", icon: FileText, exact: false },
  ],
  [
    { to: "/users", label: "Users", icon: Users, exact: false },
    { to: "/audit", label: "Audit log", icon: ScrollText, exact: false },
  ],
  [
    { to: "/api-keys", label: "API keys", icon: KeyRound, exact: false },
    { to: "/playground", label: "Playground", icon: TerminalSquare, exact: false },
  ],
] as const;

function useTheme() {
  const [dark, setDark] = useState(() => {
    if (typeof window === "undefined") return false;
    const stored = window.localStorage.getItem("fiskhub.theme");
    if (stored) return stored === "dark";
    return window.matchMedia("(prefers-color-scheme: dark)").matches;
  });

  useEffect(() => {
    document.documentElement.classList.toggle("dark", dark);
    window.localStorage.setItem("fiskhub.theme", dark ? "dark" : "light");
  }, [dark]);

  return { dark, toggle: () => setDark((current) => !current) };
}

export function AppShell() {
  const session = useSession();
  const navigate = useNavigate();
  const logout = useLogout();
  const { dark, toggle } = useTheme();
  const [navOpen, setNavOpen] = useState(false);
  const pathname = useRouterState({ select: (state) => state.location.pathname });

  // Any 401 anywhere in the app bounces to the sign-in screen.
  useEffect(() => {
    const onUnauthenticated = () => {
      void navigate({ to: "/login" });
    };
    window.addEventListener(UNAUTHENTICATED_EVENT, onUnauthenticated);
    return () => window.removeEventListener(UNAUTHENTICATED_EVENT, onUnauthenticated);
  }, [navigate]);

  useEffect(() => {
    if (session.isError) void navigate({ to: "/login" });
  }, [session.isError, navigate]);

  // Close the mobile nav whenever the route changes.
  useEffect(() => setNavOpen(false), [pathname]);

  if (session.isLoading) {
    return (
      <div className="mx-auto max-w-3xl p-8">
        <LoadingState label="your session" rows={3} />
      </div>
    );
  }

  if (!session.data) return null;

  const { user, tenant } = session.data;

  return (
    <CrossTenantProvider
      available={user.is_platform_admin}
      ownTenantId={tenant.id}
      ownTenantName={tenant.name}
    >
      <div className="min-h-screen lg:grid lg:grid-cols-[16rem_1fr]">
      <a href="#main" className="skip-link">
        Skip to main content
      </a>

      <header className="flex items-center justify-between gap-3 border-b border-border px-4 py-3 lg:hidden">
        <span className="font-semibold tracking-tight">FiskHub</span>
        <Button
          variant="outline"
          size="sm"
          aria-expanded={navOpen}
          aria-controls="primary-nav"
          onClick={() => setNavOpen((open) => !open)}
        >
          <Menu aria-hidden="true" />
          Menu
        </Button>
      </header>

      <div
        id="primary-nav"
        className={cn(
          "border-b border-border bg-card lg:sticky lg:top-0 lg:h-screen lg:border-b-0 lg:border-r",
          navOpen ? "block" : "hidden lg:block",
        )}
      >
        <div className="flex h-full flex-col gap-4 p-4">
          <div className="hidden lg:block">
            <p className="text-lg font-semibold tracking-tight">FiskHub</p>
            <TenantLine tenantName={tenant.name} />
          </div>

          <nav aria-label="Primary" className="flex-1">
            {NAV_GROUPS.map((group, index) => (
              <ul
                key={group[0].to}
                className={cn(
                  "space-y-0.5",
                  // Every group but the first is preceded by a rule.
                  index > 0 && "mt-2 border-t border-border pt-2",
                )}
              >
                {group.map((item) => {
                  const active = item.exact ? pathname === item.to : pathname.startsWith(item.to);
                  const Icon = item.icon;
                  return (
                    <li key={item.to}>
                      <Link
                        to={item.to}
                        aria-current={active ? "page" : undefined}
                        className={cn(
                          "flex items-center gap-2.5 rounded-md px-3 py-2 text-sm font-medium transition-colors",
                          active
                            ? "bg-primary/10 text-primary"
                            : "text-muted-foreground hover:bg-accent hover:text-accent-foreground",
                        )}
                      >
                        <Icon aria-hidden="true" className="size-4 shrink-0" />
                        {item.label}
                      </Link>
                    </li>
                  );
                })}
              </ul>
            ))}
          </nav>

          <div className="space-y-2 border-t border-border pt-3">
            <div className="space-y-1">
              <p className="truncate text-sm font-medium" title={user.email}>
                {user.email}
              </p>
              <div className="flex flex-wrap gap-1">
                <Badge tone="outline" className="capitalize">
                  {user.role}
                </Badge>
                {user.is_platform_admin ? (
                  <Badge
                    tone="warning"
                    title="Platform administrator: may list companies and devices across every tenant. Read-only."
                  >
                    <span aria-hidden="true">◈</span>
                    Platform admin
                  </Badge>
                ) : null}
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button variant="ghost" size="sm" onClick={toggle} aria-pressed={dark}>
                {dark ? <Sun aria-hidden="true" /> : <Moon aria-hidden="true" />}
                {dark ? "Light" : "Dark"}
              </Button>
              <Button
                variant="ghost"
                size="sm"
                loading={logout.isPending}
                onClick={() =>
                  logout.mutate(undefined, {
                    onSettled: () => {
                      void navigate({ to: "/login" });
                    },
                  })
                }
              >
                <LogOut aria-hidden="true" />
                Sign out
              </Button>
            </div>
            <BuildStamp />
          </div>
        </div>
      </div>

        <main id="main" className="min-w-0 space-y-6 p-4 sm:p-6 lg:p-8">
          <CrossTenantBanner />
          <CertificateBanner />
          <Outlet />
        </main>
      </div>
    </CrossTenantProvider>
  );
}

/**
 * The tenant the operator is working inside — overwritten while the
 * cross-tenant view is on, so the sidebar never quietly shows one tenant's name
 * over a table listing three others.
 */
function TenantLine({ tenantName }: { tenantName: string }) {
  const { enabled } = useCrossTenant();
  if (enabled) {
    return (
      <p className="truncate text-xs font-semibold text-[hsl(var(--warning))]">
        All tenants · cross-tenant view
      </p>
    );
  }
  return (
    <p className="truncate text-xs text-muted-foreground" title={tenantName}>
      {tenantName}
    </p>
  );
}
